import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Palmtree, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { getSiteProfile } from "../lib/site-profile";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const siteProfile = getSiteProfile();

  const sessionId = searchParams.get("session_id") || searchParams.get("token");
  const provider = searchParams.get("provider") || "stripe";

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (!sessionId) {
      setStatus("failed");
      return;
    }

    if (attempts >= maxAttempts) {
      setStatus("failed");
      toast.error("Payment status check timed out");
      return;
    }

    try {
      if (provider === "paypal" && attempts === 0) {
        await axios.post(
          `${API}/payments/paypal/capture`,
          { session_id: sessionId },
          { withCredentials: true }
        );
      }

      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        params: provider === "paypal" ? { provider: "paypal" } : {},
        withCredentials: true,
      });

      setPaymentDetails(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        toast.success("Payment successful — welcome, Explorer PRO!");
        await checkAuth();
        return;
      }
      if (response.data.status === "expired") {
        setStatus("failed");
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Payment status error:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus("failed");
      }
    }
  }, [checkAuth, sessionId, provider]);

  useEffect(() => {
    pollPaymentStatus();
  }, [pollPaymentStatus]);

  const displayAmount = () => {
    if (!paymentDetails) return null;
    if (paymentDetails.amount != null) {
      return `$${Number(paymentDetails.amount).toFixed(2)}`;
    }
    if (paymentDetails.amount_total != null) {
      const dollars = paymentDetails.amount_total > 1000
        ? paymentDetails.amount_total / 100
        : paymentDetails.amount_total;
      return `$${Number(dollars).toFixed(2)}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <Palmtree className="w-6 h-6 text-white" />
          </div>
          <span className="font-serif font-bold text-xl">{siteProfile.brandName}</span>
        </Link>

        {status === "checking" && (
          <div className="bg-white rounded-3xl p-8 shadow-card">
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-serif font-bold text-2xl mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">Confirming with {provider === "paypal" ? "PayPal" : "our processor"}…</p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white rounded-3xl p-8 shadow-card">
            <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-accent" />
            </div>
            <h1 className="font-serif font-bold text-2xl mb-2">You&apos;re all set!</h1>
            <p className="text-muted-foreground mb-6">
              Subscription active. Explore the full Caribbean map, saved searches, and booking perks.
            </p>
            {displayAmount() && (
              <div className="bg-muted rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-bold">{displayAmount()}</span>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <Link to="/discover">
                <Button className="w-full btn-primary">Explore with PRO</Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full">Go to Dashboard</Button>
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="bg-white rounded-3xl p-8 shadow-card">
            <div className="w-20 h-20 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
            <h1 className="font-serif font-bold text-2xl mb-2">Payment Not Confirmed</h1>
            <p className="text-muted-foreground mb-6">
              We could not verify payment yet. If PayPal shows completed, refresh or contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/premium">
                <Button className="w-full btn-primary">Try Again</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">Back to Home</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
